import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { get } from 'lodash';
import { compose } from 'utils/compose';
import { toast } from 'react-toastify';
import { inject, observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { FormControl, RadioGroup, Radio, FormControlLabel, Dialog, DialogContent, CircularProgress } from '@mui/material';

import DialogHeader from '../Dialog/DialogHeader';
import { DialogFooter, DialogActionButton } from 'components/Dialog';

import { updateQuestionnaire } from 'utils/api';
import useSearchParams from 'hooks/useSearchParams';
import {
  APARTMENT_COMPLEX,
  COMMERCIAL_BUILDING,
  ADDRESS_QUESTIONNAIRE,
  ADDRESS_TYPE_WITHOUT_BUSINESS_HOURS,
  ADDRESS_TYPE_WITH_BUSINESS_HOURS,
} from 'constants/common';

const PREFIX = 'AddressType';

const classes = {
  title: `${PREFIX}-title`,
  radioGroup: `${PREFIX}-radioGroup`,
  text: `${PREFIX}-text`,
  radio: `${PREFIX}-radio`,
  label: `${PREFIX}-label`,
  notice: `${PREFIX}-notice`,
};

const Root = styled('section')(({ theme }) => ({
  [`& .${classes.title}`]: {
    color: theme.colorTokens.onSurfaceTextDisplay,
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: 0,
  },

  [`& .${classes.radioGroup}`]: {
    [theme.breakpoints.up('sm')]: {
      columnGap: '3rem',
      flexDirection: 'row',
      minHeight: '60px',
    },
  },

  [`& .${classes.text}`]: {
    color: theme.colorTokens.onSurfaceTextTitle,
  },

  [`& .${classes.radio}`]: {
    padding: '9px',
  },

  [`& .${classes.label}`]: {
    color: theme.colorTokens.onSurfaceTextDisplay,
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 1,
  },

  [`& .${classes.notice}`]: {
    color: '#fff',
    fontSize: '14px',
    fontWeight: 500,
  },
}));

function AddressType({ store }) {
  const { trackingCode } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openConfirm, setOpenConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { deliveryStore } = store;
  const { delivery, addressTypes } = deliveryStore;
  const shipmentID = delivery?.shipment?.id;
  const addressType = get(delivery, 'recipient-questionnaire.address_type') || '';

  const [type, setType] = useState(addressType);
  const options = addressTypes ? Object.keys(addressTypes).map((key) => ({ value: key, label: addressTypes[key] })) : [];

  useEffect(() => {
    setType(addressType);
  }, [addressType]);

  const onSuccess = (response) => {
    if (response.status !== 200)
      toast.error(<span className={classes.notice}>{response?.data?.message || 'Something went wrong. Please try again!'}</span>, {
        hideProgressBar: true,
        theme: 'colored',
      });
  };

  const onError = (error) => {
    toast.error(<span className={classes.notice}>{error?.message || 'Something went wrong. Please try again!'}</span>, {
      hideProgressBar: true,
      theme: 'colored',
    });
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
    setSubmitting(false);
    setType(addressType);
  };

  const handleConfirmUpdate = async () => {
    setSubmitting(true);
    try {
      const response = await updateQuestionnaire(trackingCode, shipmentID, { address_type: type });
      onSuccess(response);
    } catch (e) {
      onError(e);
    }
    setOpenConfirm(false);
    setSubmitting(false);
    postUpdate(type);
  };

  const handleChange = (e) => {
    const { value } = e.target;
    setType(value);
    if (ADDRESS_TYPE_WITHOUT_BUSINESS_HOURS.includes(value) && (delivery?.business_hours || ADDRESS_TYPE_WITH_BUSINESS_HOURS.includes(addressType))) {
      setOpenConfirm(true);
      return;
    } else {
      updateQuestionnaire(trackingCode, shipmentID, { address_type: value }).then(onSuccess).catch(onError);
    }
    postUpdate(value);
  };

  const postUpdate = (newAddressType) => {
    deliveryStore.setAddressType(newAddressType);
    if ([APARTMENT_COMPLEX, COMMERCIAL_BUILDING].includes(newAddressType)) {
      searchParams.delete('dialog');
      searchParams.set('reason', ADDRESS_QUESTIONNAIRE);
      setSearchParams(searchParams);
    }
  };

  return (
    <Root>
      <h4 className={classes.title}>What is your address type?</h4>
      <FormControl component="fieldset" fullWidth color="primary">
        <RadioGroup aria-label="address_type" name="address_type" value={type} onChange={handleChange} className={classes.radioGroup}>
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio color="primary" classes={{ colorPrimary: classes.radio }} />}
              label={option.label}
              classes={{ label: classes.label }}
            />
          ))}
        </RadioGroup>
      </FormControl>
      <Dialog open={openConfirm} onClose={handleCloseConfirm} maxWidth="md" className={classes.root}>
        <DialogHeader title="Confirm Update Delivery Info" />
        <DialogContent>
          <p className={classes.text} style={{ margin: 0 }}>
            Your business hours will be deleted after this action. Are you sure you want to update?
          </p>
        </DialogContent>
        <DialogFooter>
          <DialogActionButton onClick={handleCloseConfirm} color="primary" variant="outlined" disableElevation>
            Cancel
          </DialogActionButton>
          <DialogActionButton onClick={handleConfirmUpdate} color="primary" variant="contained" disableElevation>
            {submitting && <CircularProgress size={16} style={{ marginRight: 8 }} />} Update Anyway
          </DialogActionButton>
        </DialogFooter>
      </Dialog>
    </Root>
  );
}

export default compose(inject('store'), observer)(AddressType);
